/**
 * @file InboxPage.tsx
 * @brief Inbox for web and desktop: what is coming up and the notifications of the last 30 days.
 *
 * @details
 * The phone's Inbox screen (DES 12-10-2026, FRONTEND InboxScreen.tsx) on
 * /app/inbox and /company/inbox, opened from the Topbar. Pills All ·
 * Upcoming · Updates.
 *
 * - Upcoming: scheduled meetings and interpretation sessions, soonest first,
 *   and a line for sessions missed in the last 24 h.
 * - Updates: the push notifications (the lock-screen text; ADR-032 keeps
 *   transcript text out of them). New ones are bold with a dot; a click opens
 *   the page the desktop notification would.
 *
 * Opening the page marks everything read on the server; the rows keep their
 * new look until the next visit.
 *
 * @author DES
 * @date 12-10-2026
 */

import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { backStateHere } from "@/shared/navigation/backState";
import {
  Bell,
  CalendarClock,
  Inbox as InboxIcon,
  ListFilter,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import { SavedCards } from "@/components/icons/SavedCards";
import { SessionTurns } from "@/components/icons/SessionTurns";
import { TodoTick } from "@/components/icons/TodoTick";
import { UsageRing } from "@/components/icons/UsageRing";
import { FormSheetTabs } from "@/components/ui/FormSheetTabs";
import { GridHeading } from "@/components/ui/GridHeading";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/shared/theme/cn";
import type { ScheduledItem } from "@/features/scheduleReminders/desktopScheduleSync";
import {
  useInboxBase,
  useInboxUpcoming,
  useInboxUpdates,
  useMarkInboxRead,
  webPathFor,
  type InboxUpdate,
} from "./api";

type Pill = "all" | "upcoming" | "updates";

/** Upcoming rows on All; the Upcoming pill shows every one (30 days). */
const UPCOMING_ON_ALL = 5;

type IconType = ComponentType<{ className?: string; strokeWidth?: number }>;

/** Push catalog icon name (BACKEND catalog.mjs `icon`) → app icon. */
const UPDATE_ICONS: Record<string, IconType> = {
  boardroom: BoardroomTable as IconType,
  note: MeetingNotesPen as IconType,
  notes: MeetingNotesPen as IconType,
  calendar: CalendarClock,
  card: SavedCards as IconType,
  usage: UsageRing as IconType,
  task: TodoTick as IconType,
  security: ShieldCheck,
  trash: Trash2,
  alert: TriangleAlert,
  session: SessionTurns as IconType,
};

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" });

function whenLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const day = same(d, today) ? "Today" : same(d, tomorrow) ? "Tomorrow" : dayFmt.format(d);
  return `${day} · ${timeFmt.format(d)}`;
}

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d` : dayFmt.format(new Date(iso));
}

function Row({
  icon: Icon,
  brand,
  title,
  sub,
  bold,
  meta,
  dot,
  onClick,
}: {
  icon: IconType;
  brand?: boolean;
  title: string;
  sub?: string;
  bold?: boolean;
  meta?: string;
  dot?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-muted/40"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60">
        <Icon className={cn("h-5 w-5", brand ? "text-brand" : "text-foreground")} strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-[15px] text-foreground",
            bold ? "font-semibold" : "font-medium",
          )}
        >
          {title}
        </span>
        {sub ? <span className="mt-0.5 line-clamp-2 block text-[13px] text-muted-foreground">{sub}</span> : null}
      </span>
      {meta || dot ? (
        <span className="flex shrink-0 flex-col items-end gap-1.5 self-start pt-0.5">
          {meta ? <span className="text-xs text-muted-foreground">{meta}</span> : null}
          {dot ? <span className="h-2 w-2 rounded-full bg-brand" aria-hidden /> : null}
        </span>
      ) : null}
    </button>
  );
}

export function InboxPage() {
  const navigate = useNavigate();
  // What Inbox opens comes back to Inbox (DES 13-10-2026).
  const backInbox = backStateHere(useLocation(), "Inbox");
  const { base } = useInboxBase();
  const [pill, setPill] = useState<Pill>("all");
  const upcomingQ = useInboxUpcoming();
  const updatesQ = useInboxUpdates();
  const markRead = useMarkInboxRead();

  const updates = useMemo<InboxUpdate[]>(
    () => (updatesQ.data?.pages ?? []).flatMap((p) => p?.items ?? []),
    [updatesQ.data],
  );
  const firstPage = updatesQ.data?.pages?.[0];
  const unread = firstPage?.unread ?? 0;
  const newest = firstPage?.items?.[0]?.seq;

  // Opening the Inbox reads everything up to the newest row.
  const { mutate } = markRead;
  useEffect(() => {
    if (newest && unread > 0) mutate(newest);
  }, [newest, unread, mutate]);

  const allUpcoming = upcomingQ.data?.items ?? [];
  const missed = upcomingQ.data?.missedRecent ?? 0;
  const upcoming = pill === "all" ? allUpcoming.slice(0, UPCOMING_ON_ALL) : allUpcoming;
  const moreUpcoming = pill === "all" ? allUpcoming.length - upcoming.length : 0;
  const showUpcoming = pill !== "updates";
  const showUpdates = pill !== "upcoming";
  const loading = upcomingQ.isLoading || updatesQ.isLoading;

  const renderUpcoming = (item: ScheduledItem) => (
    <Row
      key={item.key}
      icon={(item.kind === "meeting" ? BoardroomTable : SessionTurns) as IconType}
      brand
      title={item.title}
      sub={`${whenLabel(item.startMs)} · ${item.kind === "meeting" ? "Meeting" : "Session"}`}
      onClick={() => navigate(item.path, { state: backInbox })}
    />
  );

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      <div className="mx-auto w-full max-w-3xl">
        <h1 className="text-[28px] font-bold leading-9 tracking-tight">Inbox</h1>
        <p className="mt-1 text-[15px] text-muted-foreground">What&apos;s coming up, and what happened</p>

        <div className="mt-5">
          <FormSheetTabs<Pill>
            label="Inbox sections"
            value={pill}
            onChange={setPill}
            tabs={[
              { id: "all", label: "All", icon: ListFilter },
              { id: "upcoming", label: "Upcoming", icon: CalendarClock },
              { id: "updates", label: unread > 0 ? `Updates (${unread})` : "Updates", icon: Bell },
            ]}
          />
        </div>

        {loading ? (
          <div role="status" aria-label="Loading inbox" className="mt-6 space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 px-3 py-3">
                <Skeleton still className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton still className="h-4 w-2/3" />
                  <Skeleton still className="mt-2 h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {showUpcoming ? (
              <section className="mt-6">
                {pill === "all" ? <GridHeading label="Upcoming" className="mb-2" /> : null}
                {missed > 0 ? (
                  <p className="px-3 pb-1 text-[13px] font-medium text-destructive">
                    {missed === 1
                      ? "You missed 1 session in the last 24 hours"
                      : `You missed ${missed} sessions in the last 24 hours`}
                  </p>
                ) : null}
                {upcoming.length > 0 ? (
                  upcoming.map(renderUpcoming)
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    Scheduled meetings and sessions will show here.
                  </p>
                )}
                {moreUpcoming > 0 ? (
                  <button
                    type="button"
                    onClick={() => setPill("upcoming")}
                    className="w-full py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    {moreUpcoming} more upcoming
                  </button>
                ) : null}
              </section>
            ) : null}

            {showUpdates ? (
              <section className="mt-6">
                {pill === "all" ? <GridHeading label="Updates" className="mb-2" /> : null}
                {updates.length > 0 ? (
                  updates.map((u) => (
                    <Row
                      key={u.seq}
                      icon={(u.icon && UPDATE_ICONS[u.icon]) || InboxIcon}
                      title={u.title}
                      sub={u.body}
                      bold={u.unread}
                      dot={u.unread}
                      meta={agoLabel(u.released_at)}
                      onClick={() => navigate(webPathFor(u.target, base), { state: backInbox })}
                    />
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    Meeting summaries, shares and reminders will show here.
                  </p>
                )}
                {updatesQ.hasNextPage ? (
                  <button
                    type="button"
                    disabled={updatesQ.isFetchingNextPage}
                    onClick={() => void updatesQ.fetchNextPage()}
                    className="w-full py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    {updatesQ.isFetchingNextPage ? "Loading…" : "Show older"}
                  </button>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
